/**
 * The program row (25 Oct 2026, D-1025-NAV): a `<nav>` of plain links, one per place — Today · Housekeeping · Companion &
 * Wellness · Reading & Services · Events · Tech Support, for the roles that have them (src/app/nav.ts topTabsFor). Tab moves
 * from link to link, Enter opens, the arrow keys do nothing here (they used to jump pages). The link for the page you are on
 * says `aria-current="page"`. Each program has an address of its own (nav.ts PROGRAM_PATH). Replaces the 1.5.17 tab strip.
 */
import { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Role } from '@shared/roles';
import { currentTopTab, topTabHref, topTabsFor, type TopTab } from './nav';
import { useProgram, type Program } from './program';

function Label({ tab }: { tab: TopTab }) {
  const { t } = useTranslation();
  if (tab === 'events') return <>{t('nav.events')}</>;
  if (tab === 'today') return <>{t('nav.today')}</>;
  if (tab === 'tech') {
    // 1.8.5: "Tech" on a phone, "Tech Support" where there is room
    return (
      <>
        {t('tech185.tabShort')}
        <span className="long">{t('tech185.tab').slice(t('tech185.tabShort').length)}</span>
      </>
    );
  }
  if (tab === 'companion' || tab === 'reading') {
    // Eli, 17 Oct 2026: "It should say Companion and Wellness" — the short word on a phone
    return (
      <>
        {t(`programs.${tab}Short`)}
        <span className="long">{t(`programs.${tab}`).slice(t(`programs.${tab}Short`).length)}</span>
      </>
    );
  }
  return <>{t(`programs.${tab}`)}</>;
}

export function ProgramNav({ role }: { role: Role }) {
  const { t } = useTranslation();
  const { setProgram } = useProgram();
  const location = useLocation();
  const available = topTabsFor(role);
  // 1.8.7 (D-1.8.7-3): the row lights only the space you are in — on a page of no program no link is current
  const current = currentTopTab(location.pathname, available);
  const row = useRef<HTMLElement>(null);
  useEffect(() => {
    // a phone shows part of the row: bring the current link into view inside the row (never the page)
    const el = row.current?.querySelector<HTMLElement>('[aria-current="page"]');
    const box = row.current;
    if (!el || !box) return;
    const left = el.offsetLeft - box.offsetLeft;
    if (left < box.scrollLeft) box.scrollLeft = left - 8;
    else if (left + el.offsetWidth > box.scrollLeft + box.clientWidth) box.scrollLeft = left + el.offsetWidth - box.clientWidth + 8;
  }, [current]);
  return (
    <nav ref={row} className="prognav" aria-label={t('nav1025.programs')} data-testid="program-nav">
      {available.map((tab) => (
        <Link
          key={tab}
          id={`program-${tab}`}
          to={topTabHref(tab)}
          aria-current={current === tab ? 'page' : undefined}
          className={current === tab ? 'active' : undefined}
          onClick={() => {
            if (tab !== 'today' && tab !== 'events' && tab !== 'tech') setProgram(tab as Program);
          }}
        >
          <Label tab={tab} />
        </Link>
      ))}
    </nav>
  );
}
